
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Progress } from "@/components/ui/progress";
import { UserCheck, UserMinus } from "lucide-react";

export const UserMetrics = () => {
  // Mock data - in a real app, this would come from an API
  const userData = [
    { month: "Jan", users: 2100, caregivers: 820, churn: 28 },
    { month: "Feb", users: 2300, caregivers: 920, churn: 25 },
    { month: "Mar", users: 2500, caregivers: 1020, churn: 30 },
    { month: "Apr", users: 2700, caregivers: 1120, churn: 22 },
    { month: "May", users: 2900, caregivers: 1220, churn: 20 },
    { month: "Jun", users: 3200, caregivers: 1300, churn: 18 },
    { month: "Jul", users: 3400, caregivers: 1380, churn: 15 },
    { month: "Aug", users: 3600, caregivers: 1450, churn: 12 }
  ];

  const retentionData = [
    { label: "1-month", value: 92 },
    { label: "3-month", value: 85 },
    { label: "6-month", value: 76 },
    { label: "12-month", value: 68 }
  ];

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card className="col-span-3 md:col-span-2">
        <CardHeader>
          <CardTitle>User Growth</CardTitle>
          <CardDescription>Total users and caregivers over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            <ChartContainer
              config={{
                users: { color: "#8B5CF6" },
                caregivers: { color: "#10B981" },
                churn: { color: "#EF4444" }
              }}
            >
              <LineChart data={userData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis 
                  yAxisId="left"
                  tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(value) => `${value}%`}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="users" 
                  stroke="#8B5CF6" 
                  strokeWidth={2}
                  name="Total Users"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="caregivers" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  name="Caregivers"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="churn" 
                  stroke="#EF4444" 
                  strokeWidth={2}
                  name="Churn Rate"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => {
                        if (name === "Churn Rate") return [`${value}%`, name];
                        return [`${value.toLocaleString()}`, name];
                      }}
                    />
                  }
                />
              </LineChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="col-span-3 md:col-span-1">
        <CardHeader>
          <CardTitle>User Retention</CardTitle>
          <CardDescription>Percentage of users who remain active</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {retentionData.map((item) => (
              <div key={item.label} className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{item.label} retention</p>
                  <p className="text-sm font-bold">{item.value}%</p>
                </div>
                <Progress value={item.value} className="h-2" />
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserCheck className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-medium">New signups</span>
                </div>
                <span className="text-xl font-bold">+248</span>
              </div>
              <p className="mt-1 text-xs text-green-500">+12.5% from last month</p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserMinus className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium">Churned</span>
                </div>
                <span className="text-xl font-bold">32</span>
              </div>
              <p className="mt-1 text-xs text-green-500">-8.3% from last month</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
